import React, { useState, useEffect, useCallback } from 'react';
import { getCurrentSession } from '../services/authService';
import './ProfilePage.css';

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';

// CUSTOM-ROLE-1: groups only — custom:role is user-writable in the ride-share pool, so it never picks a role.
function getRole(userClaims) {
  const groups = userClaims['cognito:groups'] || [];
  if (groups.includes('operations') || groups.includes('ops') || groups.includes('admin')) return 'operations';
  if (groups.includes('driver') || groups.includes('drivers')) return 'driver';
  return 'customer';
}

function ProfilePage({ authSession, onLogout }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = authSession?.user?.sub;
  const userClaims = authSession?.user || {};
  const role = getRole(userClaims);

  const fetchProfile = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      let token = authSession?.idToken;
      try {
        const freshSession = await getCurrentSession();
        token = freshSession.idToken;
      } catch (e) { /* use existing */ }

      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Call different API based on role
      const endpoint = role === 'driver'
        ? `${API_BASE}/api/drivers/by-user/${userId}`
        : `${API_BASE}/api/customers/by-user/${userId}`;

      const res = await fetch(endpoint, { headers });
      if (res.status === 404) {
        setProfile(null);
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProfile(data);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [userId, authSession, role]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page">
        <div className="profile-error">
          <p>{error}</p>
          <button className="profile-retry-btn" onClick={fetchProfile}>Retry</button>
        </div>
      </div>
    );
  }

  const displayName = profile
    ? [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(' ')
    : [userClaims.given_name, userClaims.family_name].filter(Boolean).join(' ') || userClaims['cognito:username'] || '-';

  const email = profile?.email || userClaims.email || '-';
  const phone = profile?.phoneNumber || userClaims.phone_number || '-';

  return (
    <div className="profile-page">
      <div className="profile-content">
        <div className="profile-avatar-section">
          <div className="profile-avatar-circle">
            {(displayName[0] || '?').toUpperCase()}
          </div>
          <h2 className="profile-name">{displayName}</h2>
        </div>

        <div className="profile-card">
          <div className="profile-field">
            <span className="profile-field-label">Email</span>
            <span className="profile-field-value">{email}</span>
          </div>
          <div className="profile-field">
            <span className="profile-field-label">Phone</span>
            <span className="profile-field-value">{phone}</span>
          </div>
        </div>

        <button className="profile-logout-btn" onClick={onLogout}>Sign out</button>
      </div>
    </div>
  );
}

export default ProfilePage;
